import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Circle, Plus, Trash2, Edit2, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function AdminChecklistTab() {
  const [groups, setGroups] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [addingTo, setAddingTo] = useState(null);
  const [newText, setNewText] = useState("");

  const load = async () => {
    try { const r = await api.get("/admin/checklist/items"); setGroups(r.data); } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    try {
      await api.put(`/admin/checklist/items/${item.id}`, { completed: !item.completed });
      load();
    } catch { toast.error("Update failed"); }
  };

  const saveEdit = async () => {
    if (!editText.trim()) { setEditingId(null); return; }
    try {
      await api.put(`/admin/checklist/items/${editingId}`, { text: editText.trim() });
      setEditingId(null); setEditText("");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.text}"?`)) return;
    try { await api.delete(`/admin/checklist/items/${item.id}`); load(); } catch { toast.error("Delete failed"); }
  };

  const addItem = async (category) => {
    if (!newText.trim()) { setAddingTo(null); return; }
    try {
      await api.post("/admin/checklist/items", { category, text: newText.trim() });
      setAddingTo(null); setNewText("");
      load();
    } catch { toast.error("Add failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-checklist-tab">
      <div className="hc-card p-6">
        <h3 className="font-display text-xl mb-2">Roadmap</h3>
        <p className="text-sm text-[#7A736E] mb-1">Click the circle to mark complete (auto-stamps the current version). Click the text to edit. Add new items per section.</p>
      </div>
      {groups.map((g) => {
        const incomplete = g.items.filter((it) => !it.completed);
        const complete = g.items.filter((it) => it.completed);
        return (
          <div key={g.key} className="hc-card p-6" data-testid={`checklist-section-${g.key}`}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h4 className="font-display text-lg">{g.label} <span className="text-xs font-normal text-[#7A736E] ml-2">{complete.length}/{g.items.length}</span></h4>
              {addingTo === g.key ? (
                <div className="flex gap-1.5 flex-wrap">
                  <Input autoFocus value={newText} onChange={(e) => setNewText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addItem(g.key); if (e.key === "Escape") setAddingTo(null); }} placeholder="New item text…" className="rounded-xl border-[#EBE5DF] w-64 h-8 text-sm" data-testid={`checklist-new-input-${g.key}`} />
                  <Button onClick={() => addItem(g.key)} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] h-8 text-xs" data-testid={`checklist-new-save-${g.key}`}><Check className="w-3 h-3" /></Button>
                  <Button onClick={() => { setAddingTo(null); setNewText(""); }} variant="outline" className="rounded-full border-[#EBE5DF] h-8 text-xs"><X className="w-3 h-3" /></Button>
                </div>
              ) : (
                <button onClick={() => { setAddingTo(g.key); setNewText(""); }} className="text-xs text-[#3A5A4A] hover:underline flex items-center gap-1" data-testid={`checklist-add-${g.key}`}>
                  <Plus className="w-3.5 h-3.5" /> Add item
                </button>
              )}
            </div>
            <ul className="space-y-1">
              {[...incomplete, ...complete].map((it) => (
                <li key={it.id} className="group flex items-start gap-3 py-1.5 px-2 -mx-2 rounded-lg hover:bg-[#F3EFEA] transition" data-testid={`checklist-item-${it.id}`}>
                  <button onClick={() => toggle(it)} className="mt-0.5 flex-shrink-0" aria-label="Toggle complete" data-testid={`checklist-toggle-${it.id}`}>
                    {it.completed ? <CheckCircle2 className="w-5 h-5 text-[#3A5A4A]" /> : <Circle className="w-5 h-5 text-[#C4BDB6] group-hover:text-[#7A736E]" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    {editingId === it.id ? (
                      <div className="flex gap-1.5">
                        <Input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditingId(null); }} className="rounded-xl border-[#EBE5DF] h-8 text-sm" data-testid={`checklist-edit-${it.id}`} />
                        <Button onClick={saveEdit} className="h-8 rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-xs"><Check className="w-3 h-3" /></Button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingId(it.id); setEditText(it.text); }} className={`text-left text-[15px] leading-relaxed w-full ${it.completed ? "line-through text-[#A39E99]" : "text-[#3D3935]"}`}>
                        {it.text}
                      </button>
                    )}
                    {it.completed && it.version_completed && (
                      <p className="text-[10px] uppercase tracking-wider text-[#3A5A4A] mt-0.5">✓ {it.version_completed}</p>
                    )}
                  </div>
                  <button onClick={() => remove(it)} className="opacity-0 group-hover:opacity-100 transition text-[#C87B67] hover:text-[#A85F4D] flex-shrink-0" aria-label="Delete" data-testid={`checklist-delete-${it.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
              {g.items.length === 0 && <p className="text-sm text-[#7A736E] py-2">Empty — add your first item above.</p>}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
